// >>> **HER ORDER, 2026-09-18 — THE CARD REPORTS WHEN ITS PICTURE DOES NOT ARRIVE (#288/#289).**
// <<< Two members: *"cards are being pulled but the images are not displaying"*, one of them adding
// that it persisted after a refresh. Measured on 2026-09-18, all 78 slugs dealt in thirty days have
// real art, the route is not auth-gated, and the service worker never caches `/api/*` — so the loss
// is on her device and the server cannot see it at all. **Now it says so, with the card and the
// browser attached.** Silent, capped, and it can never break the picture it is watching.
import { reportClientLoss, LOSS_KIND } from "../lib/clientLoss";

// R8-4 — THE ONE CARD IMAGE, LIFTED OUT OF App.tsx VERBATIM, AND THE ROUND'S GATE MIGRATION.
//
// N4-1's law below is the one every tarot surface on the site depends on, and until now it was held
// by `scripts/t6-card-visual.mjs` MATCHING SOURCE TEXT: that `function TarotCardImage` appeared
// exactly once, and that the `cardImageClass(orientation, ...)` call was spelled the one right way.
// That is a check of the spelling, not of the behaviour — it would have passed a component that
// built the class correctly and then never put it on the img.
//
// Out here the component takes props, holds no state and calls no hook, so the gate CALLS it: a
// reversed card is proved to carry `is-reversed` ON THE IMG, a surface layering its own class on top
// is proved to keep both, and a card with no art is proved to name itself rather than render a
// broken image. `t6-card-visual.mjs` is migrated onto exactly that, which is the upgrade R5 and R6
// each took — presence of text becomes behaviour.
//
// Moved VERBATIM.

// N4-1 — ONE CARD IMAGE, EVERYWHERE. A reversed card is a different card: it is drawn upside down and it
// must LOOK upside down on every surface that shows it. It did not. The daily card in My Room drew the art
// the right way up while its own caption said "(reversed)", and its zoom overlay was never told the
// orientation at all, so the member was looking at one thing and reading another.
//
// The cause was five separate img tags each remembering the rule for themselves. So there is now ONE
// component and every card surface goes through it: the rotation lives here, in one place, and a new
// surface cannot forget it. `cardImageClass` exists for the surfaces that own their own <img> for layout
// reasons, so even those cannot spell the class wrong.
export function cardImageClass(orientation: string | undefined, base: string) {
  return orientation === "reversed" ? `${base} is-reversed` : base;
}

// >>> **IT #288/#289 — A PICTURE THAT STUMBLES TRIES AGAIN BEFORE IT IS CALLED LOST.** <<<
//
// What her 2026-09-18 beacon surfaced: real `/api/assets/tarot/<slug>` IMG failures on iPhones,
// intermittent, on card files of 190-416 KB — the size a weak signal drops mid-body. The component
// reported the loss and LEFT THE CARD BLANK: one dropped packet was a permanently missing picture
// in her conversation (#288: "cards are being pulled but the images are not displaying"). Now a
// failed load retries itself, twice, with room for the signal to come back and a cache-busting
// query so Safari cannot replay its cached failure — and ONLY a card that failed all three loads
// is reported, so the beacon keeps meaning "truly lost" rather than "stumbled once".
//
// **THE PLAN IS A PURE LAW** so the gate drives it without a DOM, and the component stays
// hookless (the t6 gate CALLS it as a function): the attempt count rides on the img element
// itself, never in state.
export const CARD_IMAGE_RETRIES = 2;
export function cardImageRetry(slug: string, tries: number) {
  const done = Number.isFinite(tries) ? Math.max(0, Number(tries)) : 0;
  if (!slug || done >= CARD_IMAGE_RETRIES) return { retry: false as const };
  const attempt = done + 1;
  return {
    retry: true as const,
    attempt,
    // 800ms, then 1600ms: a beat for the signal, never a spinner's worth of waiting.
    delayMs: 800 * attempt,
    src: `/api/assets/tarot/${slug}?retry=${attempt}`,
  };
}

export function TarotCardImage({ slug, name, orientation, className = "", ...rest }: {
  slug: string; name: string; orientation?: string; className?: string;
  loading?: "lazy" | "eager"; draggable?: boolean; onContextMenu?: (e: React.MouseEvent) => void;
}) {
  if (!slug) return <span className="tarot-card-slot__noart">{name}</span>;
  // `tarot-card-img` is always on, so ONE css rule turns every reversed card on every surface. A surface
  // that needs its own hover or layout treatment layers on top of that; it cannot lose the rotation.
  return (
    <img
      className={cardImageClass(orientation, `tarot-card-img${className ? ` ${className}` : ""}`)}
      src={`/api/assets/tarot/${slug}`}
      alt={orientation === "reversed" ? `${name}, reversed` : name}
      // The ONE place every card on this site is drawn, so the retry and the report cannot be
      // forgotten by a new surface — the same argument that put the rotation here in the first
      // place. The plan is `cardImageRetry`; only a card that failed every attempt is reported.
      onError={(e) => {
        const img = e.currentTarget;
        const plan = cardImageRetry(slug, Number(img.dataset.tries || "0"));
        if (plan.retry) {
          img.dataset.tries = String(plan.attempt);
          window.setTimeout(() => { img.src = plan.src; }, plan.delayMs);
          return;
        }
        reportClientLoss(LOSS_KIND.cardImage, { url: `/api/assets/tarot/${slug}`, detail: name });
      }}
      {...rest}
    />
  );
}

